/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";

// antd:
import { Form } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getAppState, getTheme, getLng, getDict, getUser, changeAppState } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

const RZdiv:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZdiv.displayName = "Div";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const lng = useAppSelector(getLng);
  const dct = useAppSelector(getDict);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath] || {};
  // Подстраховка для раннего рендера и неполного state: раньше здесь был риск падения на elState.attributes.
  if (!elState.attributes) { elState.attributes = {...attributes} }
  // Берем актуальные атрибуты из Redux, но сохраняем XML как исходный fallback.
  const divAttributes = useMemo(() => ({...attributes, ...elState.attributes}), [attributes, elState.attributes]);
  const {align, color, backgroundColor, borders} = divAttributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    // Проверяем существование parentState, чтобы Div не падал в промежуточных состояниях дерева.
    return Boolean(parentState && parentState.name === "form");
  }, [appState, fullPath]);

  // Права считаем уже по "слитым" атрибутам, чтобы dynamic attributes из Redux работали так же, как стартовые XML.
  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, divAttributes)), [user, divAttributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    // if (!rz.arrContains(userClss, (/-theme-/igm))) { userClss.push("rz-theme-d5") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!~userClss.indexOf("rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    if (align) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      (align || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt:string) => {
        if ((/^(left|center|right|between|around|stretch|top|middle|bottom|row|column|wrap|nowrap|first|last|none|auto|\d+)/igm).test(tt) && !~userClss.indexOf("uk-flex-" + tt)) {
          userClss.push("uk-flex-" + tt);
        }
      });
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, borders, align, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor) {
      if (bclr) { est.backgroundColor = bclr }
      else { est.backgroundColor = (theme === "light" ? "#fff" : "#141414") }
    }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  // title2 нужен для автоподписи Form.Item, поэтому тоже читаем из runtime-атрибутов Redux.
  const title2 = useMemo<string | undefined>(() => (divAttributes["title2_" + lng] || divAttributes.title2 || divAttributes["title2_" + rlng]), [divAttributes, lng, rlng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // tooltip переведен на divAttributes по той же причине: его можно менять через changeState.
  const tooltip = useMemo<string | undefined>(() => (divAttributes["tooltip_" + lng] || divAttributes.tooltip || divAttributes["tooltip_" + rlng]), [divAttributes, lng, rlng]);

  const onClick = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    if (fnc.config?.isDev) console.log({action: "<Div key='" + key + "'> -> onClick()"});
    // Раньше disabled добавлял только CSS-класс, но не блокировал сам click-обработчик.
    if (elState?.disabled || permissions.disabled === true) { return }
    if (rootNamespace && (divAttributes.onClick || divAttributes.onclick) && rootNamespace[divAttributes.onClick || divAttributes.onclick]) {
      const clientFNC = rootNamespace[divAttributes.onClick || divAttributes.onclick];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes: divAttributes, key, user, lng, dct}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes: divAttributes, key, user, lng, dct}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
      }
    }
  }, [key, rootNamespace, divAttributes, rootAttributes.key, elState?.disabled, permissions.disabled, user, lng, dct, dispatch, navigate]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // eslint-disable-next-line max-len
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return wrapperForm(
    <DivE key={key} id={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip} onClick={onClick} >
      {elState?.value || children}
    </DivE>
  );
}

export default RZdiv;
/* eslint-enable @typescript-eslint/no-unused-vars */
